import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Upload } from "lucide-react";
import { apiClient } from "@/lib/auth";

const empty = { business_name: "", slug: "", access_code: "", accent_color: "#06b6d4", brief_id: "", notes: "" };
const inputCls = "rounded-lg bg-navy-950 border border-white/10 focus:border-brand-cyan focus:outline-none px-3 py-2 text-white font-mono text-sm";

export default function TemplateDemoCreator({ briefs, onCreated }) {
  const [draft, setDraft] = useState(empty);
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  const create = async () => {
    if (!draft.business_name.trim()) return toast.error("Company name is required");
    if (!/^\d{5}$/.test(draft.access_code)) return toast.error("Code must be exactly 5 digits");
    setBusy(true);
    try {
      const { data } = await apiClient.post("/admin/demo-directory", {
        ...draft, template: "veroogle", slug: draft.slug.trim() || null, brief_id: draft.brief_id || null,
      });
      if (logo) {
        const fd = new FormData();
        fd.append("file", logo);
        await apiClient.post(`/admin/demos/${data.slug}/intro/logo`, fd).catch(() => toast.error("Demo created, but the logo upload failed"));
      }
      toast.success(`${data.business_name} demo created from the VEROOGLE template`);
      setDraft(empty);
      setLogo(null);
      onCreated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create demo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="template-demo-creator" className="mt-8 rounded-2xl border border-emerald-400/30 bg-gradient-to-br from-emerald-500/[0.06] via-navy-900/60 to-brand-cyan/[0.05] backdrop-blur-md p-5">
      <div className="flex items-center gap-2 text-emerald-300 font-mono text-[11px] tracking-[0.2em] uppercase mb-1">
        <Sparkles className="w-3.5 h-3.5" /> New demo from template
      </div>
      <p className="text-slate-400 text-sm mb-4">
        Clones the full VEROOGLE engine (5 phases, guided tour, intro and finale) with the new company name swapped in.
        Edit the wording on the demo page, then record the voice tour when you're ready.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_160px_130px_56px] gap-3">
        <input value={draft.business_name} onChange={set("business_name")} placeholder="Company name" data-testid="template-input-name" className={inputCls} />
        <input value={draft.slug} onChange={set("slug")} placeholder="slug (optional)" data-testid="template-input-slug" className={inputCls} />
        <input value={draft.access_code} onChange={(e) => setDraft((d) => ({ ...d, access_code: e.target.value.replace(/\D/g, "").slice(0, 5) }))}
          placeholder="5-digit code" inputMode="numeric" data-testid="template-input-code" className={`${inputCls} tracking-[0.3em] text-center`} />
        <input type="color" value={draft.accent_color} onChange={set("accent_color")} title="Accent color" data-testid="template-input-accent"
          className="h-[38px] w-full rounded-lg bg-navy-950 border border-white/10 cursor-pointer p-1" />
      </div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto_auto] gap-3">
        <select value={draft.brief_id} onChange={set("brief_id")} data-testid="template-input-audit" className={inputCls}>
          <option value="">No Friction Audit (savings shown as $0)</option>
          {briefs.map((b) => <option key={b.id} value={b.id}>{`${b.business_name || b.full_name} · audit`}</option>)}
        </select>
        <input value={draft.notes} onChange={set("notes")} placeholder="Internal notes (optional)" data-testid="template-input-notes" className={`${inputCls} text-slate-300`} />
        <label data-testid="template-input-logo-label" className="inline-flex items-center justify-center gap-2 rounded-lg border border-dashed border-white/20 hover:border-brand-cyan/60 px-3 py-2 text-slate-300 font-mono text-xs cursor-pointer transition-colors max-w-[220px] truncate">
          <Upload className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{logo ? logo.name : "Logo (optional)"}</span>
          <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" data-testid="template-input-logo"
            onChange={(e) => setLogo(e.target.files?.[0] || null)} />
        </label>
        <button type="button" onClick={create} disabled={busy} data-testid="template-create-btn"
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-emerald-500 to-brand-cyan text-navy-950 font-mono text-xs font-semibold tracking-[0.14em] uppercase px-4 py-2 hover:brightness-110 transition-[filter] disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Create demo
        </button>
      </div>
    </div>
  );
}
